/**
 * Scout — the floating in-platform help assistant.
 *
 * A launcher bubble fixed to the bottom-right of every tenant page; opens
 * a compact chat panel backed by /api/companies/:id/assistant/*. Scout is
 * NOT an agent — it runs in-process on the platform with read-only,
 * tenant-scoped diagnostic tools, so this widget deliberately does not
 * reuse the workspace ChatSurface (no sessions list, files, or @mentions).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { HelpCircle, LifeBuoy, Loader2, MessageCircle, RotateCcw, Send, Sparkles, X, Check, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/tenant-context";
import { MarkdownRenderer } from "@/components/chat/markdown-renderer";
import { SupportPanel } from "./support-panel";
import {
  assistantApi,
  type AssistantPageContext,
  type AssistantMessage,
} from "@/api/assistant";

const ASSISTANT_NAME = "Scout";

const TOOL_LABELS: Record<string, string> = {
  search_help: "Searching help articles",
  read_help_article: "Reading help article",
  list_agents: "Looking up your agents",
  get_agent_health: "Checking agent health",
  list_workflows: "Looking up workflows",
  list_workflow_runs: "Checking workflow runs",
  get_workflow_run: "Inspecting the run timeline",
  list_mcp_servers: "Checking MCP servers",
  check_notification_channels: "Checking your notification options",
  create_support_ticket: "Logging your support request",
  offer_ui_guide: "Preparing a walkthrough",
};

interface ToolStep {
  id: string;
  name: string;
  status: "running" | "done" | "error";
}

interface GuideOffer {
  guideId: string;
  title: string;
}

interface LocalMessage {
  role: "user" | "assistant";
  content: string;
  toolSteps?: ToolStep[];
  guideOffers?: GuideOffer[];
}

const UUID_RE = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

function pageContextFromPath(pathname: string): AssistantPageContext {
  const ctx: AssistantPageContext = { path: pathname };
  const agent = pathname.match(new RegExp(`/agents/(${UUID_RE})`));
  if (agent) ctx.agentId = agent[1];
  const workflow = pathname.match(new RegExp(`/workflows/(${UUID_RE})`));
  if (workflow) ctx.workflowId = workflow[1];
  const run = pathname.match(new RegExp(`/runs/(${UUID_RE})`));
  if (run) ctx.runId = run[1];
  return ctx;
}

function ToolStepRow({ step }: { step: ToolStep }) {
  return (
    <div className="flex items-center gap-2 text-[12px] text-text-muted">
      {step.status === "running" ? (
        <Loader2 size={12} className="animate-spin text-accent" />
      ) : step.status === "error" ? (
        <Wrench size={12} className="text-danger" />
      ) : (
        <Check size={12} className="text-accent" />
      )}
      <span>{TOOL_LABELS[step.name] ?? step.name}</span>
    </div>
  );
}

const SUGGESTIONS = [
  "How do I build a workflow?",
  "Why did my last workflow run fail?",
  "Is one of my agents having problems?",
  "What can I do on this platform?",
];

export function ScoutWidget() {
  const { tenantId, isLoading: tenantLoading } = useTenant();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"chat" | "support">("chat");
  const [supportTicketId, setSupportTicketId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [streamSteps, setStreamSteps] = useState<ToolStep[]>([]);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Bell notifications deep-link with ?supportTicket=<id> — open the panel
  // straight onto that ticket, then strip the param.
  useEffect(() => {
    const ticketId = searchParams.get("supportTicket");
    if (!ticketId) return;
    setSupportTicketId(ticketId);
    setView("support");
    setOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("supportTicket");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Lazy-load history the first time the panel opens.
  useEffect(() => {
    if (!open || loaded || !tenantId) return;
    assistantApi
      .getSession(tenantId)
      .then((res) => {
        setMessages(
          res.messages.map((m: AssistantMessage) => ({
            role: m.role,
            content: m.content,
            toolSteps: m.metadata?.toolSteps?.map((s) => ({
              id: s.id,
              name: s.name,
              status: (s.status as ToolStep["status"]) ?? "done",
            })),
            guideOffers: m.metadata?.guideOffers,
          })),
        );
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [open, loaded, tenantId]);

  // Follow the newest content only while the user is near the bottom, so
  // scrolling up to read earlier output isn't interrupted mid-generation.
  const nearBottomRef = useRef(true);
  const handleScoutScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
  };
  // Opening the widget always jumps to the latest.
  useEffect(() => {
    if (open) nearBottomRef.current = true;
  }, [open]);
  useEffect(() => {
    if (!nearBottomRef.current) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streamText, streamSteps, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, streaming]);

  const send = useCallback(
    async (text: string) => {
      const message = text.trim();
      if (!message || streaming || !tenantId) return;
      setError(null);
      setInput("");
      setMessages((prev) => [...prev, { role: "user", content: message }]);
      setStreaming(true);
      setStreamText("");
      setStreamSteps([]);

      let accumulated = "";
      let steps: ToolStep[] = [];
      let offers: GuideOffer[] = [];
      try {
        const res = await fetch(`/api/companies/${tenantId}/assistant/messages`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message,
            context: pageContextFromPath(location.pathname),
          }),
        });
        if (!res.ok || !res.body) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `Request failed (${res.status})`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            let event: Record<string, unknown>;
            try {
              event = JSON.parse(line.slice(6));
            } catch {
              continue;
            }
            if (event.type === "text_delta" && typeof event.delta === "string") {
              accumulated += event.delta;
              setStreamText(accumulated);
            } else if (event.type === "tool_call") {
              const id = String(event.id ?? "");
              const status = event.status as ToolStep["status"];
              steps = steps.some((s) => s.id === id)
                ? steps.map((s) => (s.id === id ? { ...s, status } : s))
                : [...steps, { id, name: String(event.name ?? ""), status }];
              setStreamSteps(steps);
            } else if (event.type === "guide_offer") {
              offers = [
                ...offers,
                { guideId: String(event.guideId ?? ""), title: String(event.title ?? "") },
              ];
            } else if (event.type === "error") {
              throw new Error(String(event.error ?? "Assistant error"));
            }
          }
        }
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: accumulated || "(no response)",
            toolSteps: steps,
            guideOffers: offers,
          },
        ]);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Something went wrong — please try again.");
        if (accumulated) {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: accumulated, toolSteps: steps, guideOffers: offers },
          ]);
        }
      } finally {
        setStreaming(false);
        setStreamText("");
        setStreamSteps([]);
      }
    },
    [streaming, tenantId, location.pathname],
  );

  const reset = useCallback(() => {
    if (!tenantId || streaming) return;
    assistantApi
      .resetSession(tenantId)
      .then(() => {
        setMessages([]);
        setError(null);
      })
      .catch(() => undefined);
  }, [tenantId, streaming]);

  if (tenantLoading || !tenantId) return null;

  return (
    <>
      {/* Launcher bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={`Open ${ASSISTANT_NAME}, the platform help assistant`}
          data-tour="scout"
          className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-accent text-white shadow-lg transition-transform hover:scale-105 hover:bg-accent-hover"
        >
          <HelpCircle size={22} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-40 flex h-[min(600px,calc(100vh-80px))] w-[380px] flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl">
          {/* Header */}
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/15 text-accent">
              <Sparkles size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold text-text-primary">{ASSISTANT_NAME}</div>
              <div className="text-[11.5px] text-text-muted">
                {view === "support" ? "Support requests" : "Platform help & troubleshooting"}
              </div>
            </div>
            <button
              onClick={() => setView((v) => (v === "chat" ? "support" : "chat"))}
              aria-label={view === "chat" ? "Support requests" : "Back to chat"}
              title={view === "chat" ? "Support requests" : "Back to chat"}
              className="rounded-md p-1.5 text-text-muted hover:bg-surface-hover hover:text-text-primary"
            >
              {view === "chat" ? <LifeBuoy size={15} /> : <MessageCircle size={15} />}
            </button>
            {view === "chat" && (
              <button
                onClick={reset}
                disabled={streaming || messages.length === 0}
                aria-label="Start a new conversation"
                title="Start a new conversation"
                className="rounded-md p-1.5 text-text-muted hover:bg-surface-hover hover:text-text-primary disabled:opacity-40"
              >
                <RotateCcw size={15} />
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="rounded-md p-1.5 text-text-muted hover:bg-surface-hover hover:text-text-primary"
            >
              <X size={16} />
            </button>
          </div>

          {view === "support" ? (
            <SupportPanel tenantId={tenantId} initialTicketId={supportTicketId} />
          ) : (
            <>
          {/* Messages */}
          <div ref={scrollRef} onScroll={handleScoutScroll} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
            {messages.length === 0 && !streaming && (
              <div className="flex h-full flex-col justify-center gap-3">
                <p className="text-center text-[13px] text-text-secondary">
                  Hi, I&apos;m {ASSISTANT_NAME}. Ask me how the platform works, or ask me to look
                  into an agent or workflow that isn&apos;t behaving.
                </p>
                <div className="flex flex-col gap-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => void send(s)}
                      className="rounded-lg border border-border bg-background px-3 py-2 text-left text-[12.5px] text-text-secondary hover:bg-surface-hover hover:text-text-primary"
                    >
                      {s}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setOpen(false);
                      window.dispatchEvent(new CustomEvent("proclaw:start-tour"));
                    }}
                    className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-left text-[12.5px] font-medium text-accent hover:bg-accent/10"
                  >
                    ▶ Take the platform tour
                  </button>
                </div>
              </div>
            )}

            {messages.map((m, i) =>
              m.role === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-accent/10 px-3.5 py-2 text-[13px] text-text-primary">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={i} className="space-y-1.5">
                  {m.toolSteps && m.toolSteps.length > 0 && (
                    <div className="space-y-1">
                      {m.toolSteps.map((s) => (
                        <ToolStepRow key={s.id} step={{ ...s, status: s.status === "running" ? "done" : s.status }} />
                      ))}
                    </div>
                  )}
                  <div className="text-[13px] text-text-primary">
                    <MarkdownRenderer content={m.content} />
                  </div>
                  {m.guideOffers && m.guideOffers.length > 0 && (
                    <div className="flex flex-col gap-1.5 pt-1">
                      {m.guideOffers.map((g) => (
                        <button
                          key={g.guideId}
                          onClick={() => {
                            setOpen(false);
                            window.dispatchEvent(
                              new CustomEvent("proclaw:start-guide", {
                                detail: { guideId: g.guideId },
                              }),
                            );
                          }}
                          className="flex items-center gap-2 self-start rounded-lg border border-accent/30 bg-accent/5 px-3 py-2 text-[12.5px] font-medium text-accent hover:bg-accent/10"
                        >
                          <Sparkles size={13} />
                          Show me — {g.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ),
            )}

            {streaming && (
              <div className="space-y-1.5">
                {streamSteps.length > 0 && (
                  <div className="space-y-1">
                    {streamSteps.map((s) => (
                      <ToolStepRow key={s.id} step={s} />
                    ))}
                  </div>
                )}
                {streamText ? (
                  <div className="text-[13px] text-text-primary">
                    <MarkdownRenderer content={streamText} />
                  </div>
                ) : streamSteps.length === 0 ? (
                  <div className="flex items-center gap-2 text-[12.5px] text-text-muted">
                    <Loader2 size={13} className="animate-spin" /> Thinking…
                  </div>
                ) : null}
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-[12.5px] text-danger">
                {error}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="shrink-0 border-t border-border p-3">
            <div className="flex items-end gap-2 rounded-xl border border-border bg-background px-3 py-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void send(input);
                  }
                }}
                placeholder={`Ask ${ASSISTANT_NAME} anything…`}
                rows={1}
                disabled={streaming}
                className="max-h-24 min-h-[22px] flex-1 resize-none bg-transparent text-[13px] text-text-primary outline-none placeholder:text-text-muted disabled:opacity-60"
              />
              <button
                onClick={() => void send(input)}
                disabled={streaming || !input.trim()}
                aria-label="Send"
                className={cn(
                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors",
                  input.trim() && !streaming
                    ? "bg-accent text-white hover:bg-accent-hover"
                    : "bg-surface-hover text-text-muted",
                )}
              >
                {streaming ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
